import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { 
  Alert, 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  CircularProgress,
  LinearProgress
} from "@mui/material";
import PlumSvg from "../component/PlumSvg";
import { tgAccountsAPI } from "../../Api";

const TGAccountsAdd = () => {
  const [phone, setPhone] = useState("");
  const [clientid, setClientId] = useState("");
  const [apikey, setApiKey] = useState("");
  const [type, setType] = useState("");
  const [alert, setAlert] = useState(null);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEdit = Boolean(id);
  
  // Auth flow states
  const [authStep, setAuthStep] = useState('idle'); // idle, connecting, wait_code, wait_password, success, error
  const [authAccountId, setAuthAccountId] = useState(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [authMessage, setAuthMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const showAlert = (type, message) => {
    setAlert(<Alert severity={type}>{message}</Alert>);
    setTimeout(() => setAlert(null), 5000);
  };

  const handleResendCode = async () => {
    if (!authAccountId) return;
    setResendLoading(true);
    try {
      await tgAccountsAPI.resendCode(authAccountId);
      showAlert('success', 'Код повторно отправлен. Проверьте SMS/Telegram.');
    } catch (e) {
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка повторной отправки кода');
    } finally {
      setResendLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!phone) newErrors.phone = "Номер телефона обязателен (формат: +7999...)";
    if (!clientid) newErrors.clientid = "Client ID обязателен (числовой api_id)";
    if (!apikey) newErrors.apikey = "API Key обязателен (api_hash строка)";
    if (!type) newErrors.type = "Тип обязателен";
    
    // Validate phone format
    if (phone && !phone.startsWith('+')) {
      newErrors.phone = "Номер должен начинаться с + (например: +7999...)";
    }
    
    // Validate clientid is numeric
    if (clientid && isNaN(Number(clientid))) {
      newErrors.clientid = "Client ID должен быть числом";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const startAuthFlow = async (accountId, skipStartAuth = false) => {
    try {
      setAuthStep('connecting');
      setAuthMessage('Подключение к Telegram...');
      setLoading(true);
      
      console.log('Starting auth for account:', accountId);
      
      // Skip startAuth call if this is a relogin (already started on backend)
      if (!skipStartAuth) {
        await tgAccountsAPI.startAuth(accountId);
      }
      
      // Poll status every 1 second for faster response
      const pollStatus = async () => {
        try {
          const statusRes = await tgAccountsAPI.getStatus(accountId);
          const status = statusRes?.data?.data?.status;
          console.log('Current status:', status);
          
          // If no status yet, continue polling
          if (!status) {
            setTimeout(pollStatus, 1000);
            return;
          }
          
          switch (status) {
            case 'wait_params':
              setAuthMessage('Установка параметров TDLib...');
              break;
            case 'wait_encryption':
              setAuthMessage('Настройка шифрования...');
              break;
            case 'wait_phone':
              setAuthMessage('Отправка номера телефона...');
              break;
            case 'wait_code':
              console.log('Setting auth step to wait_code');
              setAuthStep('wait_code');
              setAuthMessage('Введите код из SMS');
              setLoading(false);
              return; // Stop polling
            case 'wait_password':
              setAuthStep('wait_password');
              setAuthMessage('Введите пароль 2FA');
              setLoading(false);
              return; // Stop polling
            case 'active':
            case 'ready':
              setAuthStep('success');
              setAuthMessage('Авторизация успешна!');
              setLoading(false);
              setTimeout(() => {
                showAlert('success', 'TG аккаунт успешно добавлен и авторизован!');
                navigate('/tg-accounts/list');
              }, 1500);
              return; // Stop polling
            case 'error':
              setAuthStep('error');
              setAuthMessage('Ошибка авторизации');
              setLoading(false);
              return; // Stop polling
            case 'need_login':
              setAuthMessage('Требуется повторная авторизация...');
              break;
            default:
              setAuthMessage(`Статус: ${status || 'неизвестно'}`);
          }
          
          // Continue polling
          setTimeout(pollStatus, 1000);
        } catch (e) {
          console.error('Status poll error:', e);
          setTimeout(pollStatus, 2000); // Retry with longer delay
        }
      };
      
      // Start polling
      setTimeout(pollStatus, 1000);
      
    } catch (e) {
      console.error('Auth start error:', e);
      setAuthStep('error');
      setAuthMessage('Ошибка запуска авторизации');
      setLoading(false);
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка авторизации');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const apiCall = isEdit
        ? tgAccountsAPI.updateAccount(id, { phone, clientid, apikey, type })
        : tgAccountsAPI.createAccount({ phone, clientid, apikey, type });
      
      const res = await apiCall;
      
      if (res.data?.success) {
        const accountId = isEdit ? id : res.data?.data?._id;
        
        if (!isEdit && accountId) {
          // Start auth flow for new account
          setAuthAccountId(accountId);
          await startAuthFlow(accountId);
        } else {
          // Just update existing account
          setLoading(false);
          showAlert('success', 'TG аккаунт обновлён!');
          navigate('/tg-accounts/list');
        }
      } else {
        setLoading(false);
        showAlert('error', 'Ошибка сохранения аккаунта');
      }
    } catch (e) {
      setLoading(false);
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка сохранения');
    }
  };

  const handleCodeSubmit = async () => {
    if (!code.trim()) return;
    
    setLoading(true);
    try {
      console.log('Submitting code:', code);
      await tgAccountsAPI.submitCode(authAccountId, code.trim());
      
      // Start polling for status change
      const pollAfterCode = async () => {
        try {
          const statusRes = await tgAccountsAPI.getStatus(authAccountId);
          const status = statusRes?.data?.data?.status;
          console.log('Status after code:', status);
          
          if (status === 'active' || status === 'ready') {
            setAuthStep('success');
            setAuthMessage('Авторизация успешна!');
            setLoading(false);
            setTimeout(() => {
              showAlert('success', 'TG аккаунт успешно авторизован!');
              navigate('/tg-accounts/list');
            }, 1500);
          } else if (status === 'wait_password') {
            setAuthStep('wait_password');
            setAuthMessage('Введите пароль 2FA');
            setLoading(false);
          } else if (status === 'error') {
            setAuthStep('error');
            setAuthMessage('Неверный код');
            setLoading(false);
          } else {
            // Continue polling
            setTimeout(pollAfterCode, 1000);
          }
        } catch (e) {
          console.error('Status poll after code error:', e);
          setTimeout(pollAfterCode, 2000);
        }
      };
      
      setTimeout(pollAfterCode, 500);
      
    } catch (e) {
      setLoading(false);
      console.error('Code submit error:', e);
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка отправки кода');
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password.trim()) return;
    
    setLoading(true);
    try {
      console.log('Submitting password');
      await tgAccountsAPI.submitPassword(authAccountId, password.trim());
      
      // Poll for final status
      const pollAfterPassword = async () => {
        try {
          const statusRes = await tgAccountsAPI.getStatus(authAccountId);
          const status = statusRes?.data?.data?.status;
          console.log('Status after password:', status);
          
          if (status === 'active' || status === 'ready') {
            setAuthStep('success');
            setAuthMessage('Авторизация успешна!');
            setLoading(false);
            setTimeout(() => {
              showAlert('success', 'TG аккаунт успешно авторизован!');
              navigate('/tg-accounts/list');
            }, 1500);
          } else if (status === 'error') {
            setAuthStep('error');
            setAuthMessage('Неверный пароль');
            setLoading(false);
          } else {
            setTimeout(pollAfterPassword, 1000);
          }
        } catch (e) {
          setTimeout(pollAfterPassword, 2000);
        }
      };
      
      setTimeout(pollAfterPassword, 500);
      
    } catch (e) {
      setLoading(false);
      console.error('Password submit error:', e);
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка отправки пароля');
    }
  };

  const resetAuthFlow = () => {
    setAuthStep('idle');
    setAuthAccountId(null);
    setCode("");
    setPassword("");
    setAuthMessage("");
    setLoading(false);
  };

  // Load existing account data for edit
  useEffect(() => {
    const load = async () => {
      if (!isEdit) return;
      try {
        const res = await tgAccountsAPI.getAccountById(id);
        const account = res.data?.data;
        if (account) {
          setPhone(account.phone || "");
          setClientId(account.clientid || "");
          setApiKey(account.apikey || "");
          setType(account.type || "");
        }
      } catch (e) {
        showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка загрузки аккаунта');
      }
    };
    load();
  }, [id, isEdit]);

  // If this is a relogin request (?relogin=1), auto-start the auth flow for edit mode
  useEffect(() => {
    if (!isEdit) return;
    const params = new URLSearchParams(location.search);
    const needRelogin = params.get('relogin') === '1';
    if (needRelogin) {
      // Kick off the auth flow just like after creation
      setAuthAccountId(id);
      // Add small delay to ensure relogin completed on backend
      setTimeout(() => {
        startAuthFlow(id, true); // Skip startAuth since relogin already started it
      }, 1000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, location.search]);

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4, p: 4, boxShadow: 3, borderRadius: 2, bgcolor: "#fefcfb" }}>
        <Typography variant="h5" align="center" gutterBottom>
          <PlumSvg /> {isEdit ? 'Редактировать TG аккаунт' : 'Добавить TG аккаунт'}
        </Typography>

        {alert}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Номер телефона"
            placeholder="+7999..."
            variant="outlined"
            fullWidth
            margin="normal"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={Boolean(errors.phone)}
            helperText={errors.phone || "Формат: +7999..."}
            disabled={loading}
            sx={{
              "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#683d62" } },
              "& .MuiInputLabel-root": { "&.Mui-focused": { color: "#683d62" } },
            }}
          />

          <TextField
            label="Client ID (API ID)"
            placeholder="12345678"
            variant="outlined"
            fullWidth
            margin="normal"
            value={clientid}
            onChange={(e) => setClientId(e.target.value)}
            error={Boolean(errors.clientid)}
            helperText={errors.clientid || "Числовой API ID из my.telegram.org"}
            disabled={loading}
            sx={{
              "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#683d62" } },
              "& .MuiInputLabel-root": { "&.Mui-focused": { color: "#683d62" } },
            }}
          />

          <TextField
            label="API Hash"
            placeholder="abcd1234..."
            variant="outlined"
            fullWidth
            margin="normal"
            value={apikey}
            onChange={(e) => setApiKey(e.target.value)}
            error={Boolean(errors.apikey)}
            helperText={errors.apikey || "API Hash из my.telegram.org"}
            disabled={loading}
            sx={{
              "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#683d62" } },
              "& .MuiInputLabel-root": { "&.Mui-focused": { color: "#683d62" } },
            }}
          />

          <TextField
            label="Тип аккаунта"
            placeholder="user/bot/etc"
            variant="outlined"
            fullWidth
            margin="normal"
            value={type}
            onChange={(e) => setType(e.target.value)}
            error={Boolean(errors.type)}
            helperText={errors.type}
            disabled={loading}
            sx={{
              "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#683d62" } },
              "& .MuiInputLabel-root": { "&.Mui-focused": { color: "#683d62" } },
            }}
          />

          <Button 
            type="submit" 
            variant="contained" 
            fullWidth 
            disabled={loading || authStep !== 'idle'}
            sx={{ mt: 3, mb: 2, backgroundColor: "#683d62", height: 48 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              isEdit ? 'Сохранить изменения' : 'Добавить и авторизовать'
            )}
          </Button>
        </form>
      </Box>

      {/* Auth Progress Dialog */}
      <Dialog 
        open={authStep !== 'idle'} 
        onClose={authStep === 'error' ? resetAuthFlow : undefined}
        maxWidth="sm"
        fullWidth
      >
        {console.log('Dialog render - authStep:', authStep, 'open:', authStep !== 'idle')}
        <DialogTitle>
          {authStep === 'connecting' && 'Авторизация Telegram'}
          {authStep === 'wait_code' && 'Введите SMS код'}
          {authStep === 'wait_password' && 'Введите пароль 2FA'}
          {authStep === 'success' && 'Успешно!'}
          {authStep === 'error' && 'Ошибка'}
        </DialogTitle>
        
        <DialogContent>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {authMessage}
          </Typography>

          {authStep === 'wait_code' && (
            <>
              {console.log('Rendering code input field')}
              <TextField
                autoFocus
                label="Код из SMS"
                placeholder="12345"
                fullWidth
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={loading}
                onKeyPress={(e) => e.key === 'Enter' && handleCodeSubmit()}
                sx={{ mt: 1 }}
              />
            </>
          )}

          {authStep === 'wait_password' && (
            <TextField
              autoFocus
              label="Пароль 2FA"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>

        <DialogActions>
          {authStep === 'error' && (
            <Button onClick={resetAuthFlow}>Закрыть</Button>
          )}
          
          {authStep === 'wait_code' && (
            <>
              <Button onClick={resetAuthFlow} disabled={loading}>Отмена</Button>
              <Button 
                onClick={handleResendCode}
                disabled={loading || resendLoading}
              >
                {resendLoading ? <CircularProgress size={20} /> : 'Отправить код повторно'}
              </Button>
              <Button 
                variant="contained" 
                onClick={handleCodeSubmit}
                disabled={loading || !code.trim()}
              >
                {loading ? <CircularProgress size={20} /> : 'Отправить'}
              </Button>
            </>
          )}
          
          {authStep === 'wait_password' && (
            <>
              <Button onClick={resetAuthFlow} disabled={loading}>Отмена</Button>
              <Button 
                variant="contained" 
                onClick={handlePasswordSubmit}
                disabled={loading || !password.trim()}
              >
                {loading ? <CircularProgress size={20} /> : 'Отправить'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TGAccountsAdd;
